<template>
  <div>
    <CalicoDashboard
      class="representative-page"
      no-content-padding
    >
      <template #drawer="{ show }">
        <CalicoDrawer
          v-if="hasUsageData"
          :show="show"
        >
          <label class="label--xsmall heading">Usage</label>
          <CalicoUsageLimitBox
            :limit="limit"
            :remaining="remaining"
            :viewed="viewed"
          />
          <CalicoUsageResets :reset-date="usage?.cycle?.endExclusive" />
          <span style="width: fit-content;">
            <button
              class="button-utility button-utility--small"
              @click="upgrade"
            >
              Upgrade Subscription
            </button>
          </span>
        </CalicoDrawer>
      </template>

      <!-- Header -->
      <template #header>
        <SearchWidget />
      </template>

      <!-- Content -->
      <template #content>
        <div class="representative-dashboard-container">
          <BetascoreTrackerWidget 
            :loading="tracker.loading"
            :items="tracker.items"
            :sort="tracker.sort"
            :pagination="tracker.pagination"
            :has-you-items="tracker.hasYouItems"
            :has-organization-items="tracker.hasOrganizationItems"
            :has-history-items="tracker.hasHistoryItems"
            @toggle-sort="tracker.toggleSort"
            @change-page="tracker.goToPage"
            @select="onSelectItem"
            @find-business="goToSearch"
          />
        </div>
      </template>
    </CalicoDashboard>

    <CalicoUsageViewProfileConfirmation
      v-if="showUsageConfirmation"
      :limit="limit"
      :remaining="remaining"
      :viewed="viewed"
      :days-valid="30"
      :confirming="confirmingView"
      :error-message="usageError"
      @confirm="confirmViewProfile"
      @close="showUsageConfirmation = false"
    />
  </div>
</template>

<script>
import CalicoDashboard from "@/components/template/dashboard/index.vue";
import CalicoDrawer from "@/components/calico/drawer/index.vue";
import SearchWidget from "@/components/widget/search/index.vue";
import BetascoreTrackerWidget from "@/components/widget/betascoreTracker/index.vue";
import CalicoUsageLimitBox from "@/components/calico/usageLimitBox/index.vue";
import CalicoUsageResets from "@/components/calico/usageResets/index.vue";
import CalicoUsageViewProfileConfirmation from "@/components/pattern/usageViewProfileConfirmation/index.vue";
import { useEmailVerification } from "@/composables/auth/useEmailVerification";
import { useScoreUsageTracker } from "@/composables/useScoreUsageTracker";
import { useUsage, deriveUsageMetrics, upgrade } from "@/composables/auth/useUsage";

export default {
  name: "RepresentativeDashboard",

  components: {
    CalicoDashboard,
    CalicoDrawer,
    SearchWidget,
    BetascoreTrackerWidget,
    CalicoUsageLimitBox,
    CalicoUsageResets,
    CalicoUsageViewProfileConfirmation
  },

  setup() {
    const {
      data: usage,
      loading,
      errorMessage,
      fetchCurrentUsage
    } = useUsage();

    const {
      hasUsageData,
      limit,
      remaining,
      viewed
    } = deriveUsageMetrics(usage, loading);

    fetchCurrentUsage();

    return {
      usage,
      usageError: errorMessage,
      hasUsageData,
      limit,
      remaining,
      viewed,
      upgrade,
      fetchCurrentUsage
    };
  },

  data() {
    return {
      recentSearches: [],
      tracker: null,
      selectedBusinessId: null,
      confirmingView: false,
      showUsageConfirmation: false
    };
  },

  async created() {
    this.tracker = useScoreUsageTracker();
    await this.tracker.preloadScopes();

    const { sendRepresentativeDashboardEmail } = useEmailVerification();
    if (this.$auth.currentUser) {
      const firstName = this.$auth.currentUser.displayName?.split(" ")[0];
      sendRepresentativeDashboardEmail(
        this.$auth.currentUser.email,
        firstName
      );
    }
  },

  mounted() {
    this.recentSearches = JSON.parse(
      localStorage.getItem("recentSearches") || "[]"
    );
  },

  methods: {
    onSelectItem({ item, isHistory }) {
      if (!item?.businessId) return;

      if (isHistory) {
        this.selectedBusinessId = item.businessId;
        this.showUsageConfirmation = true;
        this.fetchCurrentUsage();
        return;
      }

      this.$router.push(`/business/${item.businessId}`);
    },
    
    async confirmViewProfile() {
      if (!this.selectedBusinessId) return;

      try {
        this.confirmingView = true;
        this.$router.push(`/business/${this.selectedBusinessId}`);
      } finally {
        this.confirmingView = false;
        this.showUsageConfirmation = false;
        this.selectedBusinessId = null;
      }
    },

    goToSearch() {
      this.$router.push("/search");
    },
  }
};
</script>

<style scoped lang="scss">
.representative-page {
  .heading {
    color: $color-neutral;
  }
  .usage-box {
    text-align: left;
  }
}
</style>